import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Edit, Calendar, Clock, MapPin, Users, Send, Bell, CheckCircle, XCircle, HelpCircle, Clock3 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  bozza: 'bg-slate-100 text-slate-800',
  programmato: 'bg-blue-100 text-blue-800',
  in_corso: 'bg-green-100 text-green-800',
  concluso: 'bg-slate-100 text-slate-800',
  annullato: 'bg-red-100 text-red-800'
};

const disponibilitaColors = {
  partecipo: 'bg-green-100 text-green-800',
  non_posso: 'bg-red-100 text-red-800',
  forse: 'bg-yellow-100 text-yellow-800',
  in_attesa: 'bg-slate-100 text-slate-600'
};

export default function EventoDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [disponibilita, setDisponibilita] = useState(null);
  const [presenze, setPresenze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filtroDisp, setFiltroDisp] = useState('tutti');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [evtRes, dispRes, presRes] = await Promise.all([
        fetch(`${API_URL}/api/eventi/${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/disponibilita/evento/${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/presenze?evento_id=${id}`, { credentials: 'include' })
      ]);

      if (evtRes.ok) setEvento(await evtRes.json());
      if (dispRes.ok) setDisponibilita(await dispRes.json());
      if (presRes.ok) setPresenze(await presRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleInviaRichiesta = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/disponibilita/richiedi/${id}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Errore nell\'invio');
    } finally {
      setSending(false);
    }
  };

  const handleInviaPromemoria = async (orePrima) => {
    try {
      const response = await fetch(`${API_URL}/api/notifiche/promemoria/${id}?ore_prima=${orePrima}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Errore nell\'invio promemoria');
    }
  };

  const handleGeneraDiario = async () => {
    try {
      const response = await fetch(`${API_URL}/api/diario/genera/${id}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Voce diario generata con successo');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const getFilteredDisponibilita = () => {
    if (!disponibilita) return [];
    switch (filtroDisp) {
      case 'confermati': return disponibilita.confermati || [];
      case 'non_disponibili': return disponibilita.non_disponibili || [];
      case 'forse': return disponibilita.forse || [];
      case 'in_attesa': return disponibilita.in_attesa || [];
      default:
        return [
          ...(disponibilita.confermati || []),
          ...(disponibilita.forse || []),
          ...(disponibilita.in_attesa || []),
          ...(disponibilita.non_disponibili || [])
        ];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!evento) {
    return <div>Evento non trovato</div>;
  }

  const totOre = presenze.reduce((sum, p) => sum + (p.ore_calcolate || 0), 0);

  return (
    <div className="space-y-6" data-testid="evento-dettaglio">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/eventi')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{evento.titolo}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={statusColors[evento.stato]}>
                {evento.stato?.replace('_', ' ').toUpperCase()}
              </Badge>
              {evento.tipologia && <span className="text-slate-500">{evento.tipologia}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/eventi/${id}/modifica`)}>
            <Edit className="w-4 h-4 mr-2" />
            Modifica
          </Button>
          <Button onClick={() => navigate(`/checkin?evento=${id}`)} className="btn-success">
            Check-in
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-[#0055BB]" />
            <div>
              <p className="text-sm text-slate-500">Data</p>
              <p className="font-semibold">{evento.data}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-[#0055BB]" />
            <div>
              <p className="text-sm text-slate-500">Orario</p>
              <p className="font-semibold">{evento.orario_inizio || 'N/D'} - {evento.orario_fine || 'N/D'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <MapPin className="w-8 h-8 text-[#0055BB]" />
            <div>
              <p className="text-sm text-slate-500">Luogo</p>
              <p className="font-semibold truncate">{evento.luogo || 'N/D'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-8 h-8 text-[#008A55]" />
            <div>
              <p className="text-sm text-slate-500">Confermati</p>
              <p className="font-semibold text-[#008A55]">{disponibilita?.totali?.confermati || 0} volontari</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="disponibilita" className="w-full">
        <TabsList>
          <TabsTrigger value="disponibilita">Disponibilità</TabsTrigger>
          <TabsTrigger value="presenze">Presenze ({presenze.length})</TabsTrigger>
          <TabsTrigger value="azioni">Azioni</TabsTrigger>
        </TabsList>

        <TabsContent value="disponibilita" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Riepilogo Disponibilità</CardTitle>
              <Button onClick={handleInviaRichiesta} disabled={sending}>
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Invio...' : 'Invia Richiesta'}
              </Button>
            </CardHeader>
            <CardContent>
              {/* Summary boxes */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-700">{disponibilita?.totali?.confermati || 0}</p>
                  <p className="text-xs text-green-600">Confermati</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <HelpCircle className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
                  <p className="text-2xl font-bold text-yellow-700">{disponibilita?.totali?.forse || 0}</p>
                  <p className="text-xs text-yellow-600">Forse</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Clock3 className="w-6 h-6 mx-auto text-slate-500 mb-1" />
                  <p className="text-2xl font-bold text-slate-600">{disponibilita?.totali?.in_attesa || 0}</p>
                  <p className="text-xs text-slate-500">In Attesa</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <XCircle className="w-6 h-6 mx-auto text-red-600 mb-1" />
                  <p className="text-2xl font-bold text-red-700">{disponibilita?.totali?.non_disponibili || 0}</p>
                  <p className="text-xs text-red-600">Non Disponibili</p>
                </div>
              </div>

              {/* Filter */}
              <div className="mb-4">
                <Select value={filtroDisp} onValueChange={setFiltroDisp}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filtra" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti</SelectItem>
                    <SelectItem value="confermati">Solo Confermati</SelectItem>
                    <SelectItem value="forse">Solo Forse</SelectItem>
                    <SelectItem value="in_attesa">In Attesa</SelectItem>
                    <SelectItem value="non_disponibili">Non Disponibili</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Volontario</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Contatto</TableHead>
                    <TableHead>Risposta</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredDisponibilita().map((d, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{d.volontario_nome || 'N/D'}</TableCell>
                      <TableCell>
                        <Badge className={disponibilitaColors[d.stato]}>
                          {d.stato === 'partecipo' ? 'Partecipo' : 
                           d.stato === 'non_posso' ? 'Non posso' :
                           d.stato === 'forse' ? 'Forse' : 'In attesa'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {d.volontario_telefono || d.volontario_email || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">
                        {d.risposta_timestamp ? new Date(d.risposta_timestamp).toLocaleString('it-IT') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="presenze" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Presenze Registrate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-4 bg-slate-50 rounded-lg flex justify-between items-center">
                <span>{presenze.length} volontari presenti</span>
                <span className="font-semibold">{totOre.toFixed(1)} ore totali</span>
              </div>
              {presenze.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Volontario</TableHead>
                      <TableHead>Check-in</TableHead>
                      <TableHead>Check-out</TableHead>
                      <TableHead>Ore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {presenze.map((p, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{p.volontario_id}</TableCell>
                        <TableCell className="text-green-600 font-mono">{p.ora_checkin || '-'}</TableCell>
                        <TableCell className="text-red-600 font-mono">{p.ora_checkout || '-'}</TableCell>
                        <TableCell>{p.ore_calcolate?.toFixed(1) || '-'} h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-slate-500 py-8">Nessuna presenza registrata</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="azioni" className="mt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#0055BB]" />
                  Invia Promemoria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">Invia un promemoria ai volontari che hanno confermato la partecipazione.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleInviaPromemoria(24)}>
                    24h Prima
                  </Button>
                  <Button variant="outline" onClick={() => handleInviaPromemoria(3)}>
                    3h Prima
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Genera Voce Diario</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">Crea una voce nel Diario Attività per questo evento completato.</p>
                <Button onClick={handleGeneraDiario} disabled={evento.stato !== 'concluso'}>
                  Genera Voce Diario
                </Button>
                {evento.stato !== 'concluso' && (
                  <p className="text-xs text-amber-600">L'evento deve essere concluso.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
